import React from 'react';
import { Modal, Typography, Space, Button, Card } from 'antd';
import {
  CheckCircleOutlined,
  RocketOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import {
  getSmallModalProps,
  getCardStyle,
  getPrimaryButtonStyle,
  emeraldColors,
} from '../../styles/modalStyles';

const { Title } = Typography;

interface CelebrationModalProps {
  fromStage: string;
  toStage: string;
  achievements: string[];
  nextSteps: string[];
  onClose: () => void;
}

const CelebrationModal: React.FC<CelebrationModalProps> = ({
  fromStage,
  toStage,
  achievements,
  nextSteps,
  onClose,
}) => {
  const getStageLabel = (stage: string) => {
    const labels = {
      assessment: '智能评估',
      preparation: '发行准备',
      issuance: '发行管理',
      'post-issuance': '存续管理',
    };
    return labels[stage as keyof typeof labels] || stage;
  };

  const getStageIcon = (stage: string) => {
    const icons = {
      assessment: '🔍',
      preparation: '📋',
      issuance: '🚀',
      'post-issuance': '📊',
    };
    return icons[stage as keyof typeof icons] || '✨';
  };

  return (
    <Modal
      {...getSmallModalProps()}
      open={true}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={false}
      styles={{
        ...getSmallModalProps().styles,
        body: {
          background: 'linear-gradient(135deg, #f8fffe 0%, #ecfdf5 100%)',
          padding: 0,
        },
      }}
    >
      <div style={{ textAlign: 'center', padding: '40px 32px' }}>
        {/* 阶段转换动画 */}
        <div
          style={{
            fontSize: '56px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <span style={{ opacity: 0.6 }}>{getStageIcon(fromStage)}</span>
          <span
            style={{
              fontSize: '32px',
              color: emeraldColors.primary,
              fontWeight: 'bold',
            }}
          >
            →
          </span>
          <span>{getStageIcon(toStage)}</span>
        </div>

        {/* 成功标题 */}
        <div
          style={{
            background: `linear-gradient(135deg, ${emeraldColors.primary} 0%, ${emeraldColors.primaryDark} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '32px',
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            <TrophyOutlined
              style={{ marginRight: '8px', color: emeraldColors.primary }}
            />
            成功进入{getStageLabel(toStage)}阶段
          </Title>
        </div>

        {/* 成就卡片 */}
        {achievements.length > 0 && (
          <Card
            size="small"
            style={getCardStyle({ textAlign: 'left', marginBottom: '16px' })}
            styles={{ body: { padding: '16px' } }}
          >
            <Title
              level={4}
              style={{
                marginBottom: '12px',
                color: emeraldColors.primaryDark,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <CheckCircleOutlined />
              您的成就
            </Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {achievements.map((achievement, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px',
                    background: 'rgba(16, 185, 129, 0.05)',
                    borderRadius: '8px',
                  }}
                >
                  <CheckCircleOutlined
                    style={{ color: emeraldColors.primary, fontSize: '16px' }}
                  />
                  <span style={{ fontSize: '14px' }}>{achievement}</span>
                </div>
              ))}
            </Space>
          </Card>
        )}

        {/* 下一步卡片 */}
        {nextSteps.length > 0 && (
          <Card
            size="small"
            style={getCardStyle({ textAlign: 'left', marginBottom: '24px' })}
            styles={{ body: { padding: '16px' } }}
          >
            <Title
              level={4}
              style={{
                marginBottom: '12px',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <RocketOutlined />
              接下来您可以
            </Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {nextSteps.map((step, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px',
                    background: 'rgba(245, 158, 11, 0.05)',
                    borderRadius: '8px',
                  }}
                >
                  <RocketOutlined
                    style={{ color: '#f59e0b', fontSize: '16px' }}
                  />
                  <span style={{ fontSize: '14px' }}>{step}</span>
                </div>
              ))}
            </Space>
          </Card>
        )}

        <Button
          type="primary"
          size="large"
          onClick={onClose}
          style={{
            ...getPrimaryButtonStyle('primary'),
            fontSize: '16px',
            height: '48px',
            padding: '0 32px',
            marginTop: '8px',
          }}
        >
          继续体验 {getStageLabel(toStage)}
        </Button>
      </div>
    </Modal>
  );
};

export default CelebrationModal;
